import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { OBJExporter } from 'three/examples/jsm/exporters/OBJExporter.js';

const SNAP = 0.1;
const MIN_SIZE = 0.5;
const MAX_SIZE = 100;
const MAX_HISTORY = 60;
const PROJECT_FORMAT = 'massa-project';
const PROJECT_VERSION = 2;
const GENERATOR = 'Massa · DØMO LAB MVP 1.3';

const STARTER_MASS = Object.freeze({
  name: 'Масса 01',
  width: 6,
  depth: 6,
  height: 6,
  profile: 'box',
  eaveRatio: 1,
  position: { x: 0, z: 0 },
  rotationY: 0,
  holes: [
    { x: 0, y: 1.4, width: 1.8, height: 2.8, grounded: true },
  ],
});

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const snap = (value, step = SNAP) => Math.round(value / step) * step;
const finiteOr = (value, fallback) => (Number.isFinite(Number(value)) ? Number(value) : fallback);
const deepCopy = (value) => JSON.parse(JSON.stringify(value));

const TOOL_COPY = {
  move: {
    title: 'Перемещение',
    help: 'Зажмите объект и перемещайте его по сетке.',
  },
  height: {
    title: 'Высота',
    help: 'Зажмите объём и ведите вверх или вниз, чтобы изменить высоту.',
  },
  cut: {
    title: 'Проём',
    help: 'Нажмите на фасад: будет создан сквозной прямоугольный вырез.',
  },
};

export class ArchitecturalModeler {
  constructor(canvas, options = {}) {
    if (!(canvas instanceof HTMLCanvasElement)) {
      throw new TypeError('ArchitecturalModeler requires a canvas element.');
    }

    this.canvas = canvas;
    this.container = canvas.parentElement;
    this.callbacks = {
      onSelectionChange: options.onSelectionChange ?? (() => {}),
      onHistoryChange: options.onHistoryChange ?? (() => {}),
      onStatus: options.onStatus ?? (() => {}),
      onToolChange: options.onToolChange ?? (() => {}),
      onModelChange: options.onModelChange ?? (() => {}),
    };

    this.tool = 'move';
    this.opening = { width: 1.2, height: 1.5, sill: 0.9 };
    this.selected = null;
    this.selectionHelper = null;
    this.dragState = null;
    this.pointerId = null;
    this.history = [];
    this.future = [];
    this.massCounter = 0;
    this.isDisposed = false;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0e100d);
    this.scene.fog = new THREE.Fog(0x0e100d, 18, 44);

    this.camera = new THREE.PerspectiveCamera(40, 1, 0.05, 200);
    this.camera.position.set(13.4, 10.6, 15.2);

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: true,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;

    this.massesGroup = new THREE.Group();
    this.massesGroup.name = 'Massa_Masses';
    this.scene.add(this.massesGroup);

    this.raycaster = new THREE.Raycaster();
    this.pointerNdc = new THREE.Vector2();
    this.groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    this.tempPoint = new THREE.Vector3();

    this.#buildEnvironment();
    this.#bindPointerEvents();

    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.07;
    this.controls.target.set(0, 3.35, 0);
    this.controls.minDistance = 5;
    this.controls.maxDistance = 42;
    this.controls.maxPolarAngle = Math.PI * 0.485;
    this.controls.update();

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.container);
    this.resize();

    this.#createDemoScene();
    this.#commitHistory(true);
    this.setTool('move');

    this.animationFrame = requestAnimationFrame(() => this.#animate());
  }

  #buildEnvironment() {
    const hemisphere = new THREE.HemisphereLight(0xe8efe0, 0x20251f, 2.25);
    this.scene.add(hemisphere);

    const keyLight = new THREE.DirectionalLight(0xffffff, 3.3);
    keyLight.position.set(10, 17, 9);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(2048, 2048);
    keyLight.shadow.camera.left = -18;
    keyLight.shadow.camera.right = 18;
    keyLight.shadow.camera.top = 18;
    keyLight.shadow.camera.bottom = -18;
    keyLight.shadow.camera.near = 1;
    keyLight.shadow.camera.far = 45;
    keyLight.shadow.bias = -0.0004;
    this.scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0x9fc8ff, 0.7);
    fillLight.position.set(-10, 7, -8);
    this.scene.add(fillLight);

    const grid = new THREE.GridHelper(40, 80, 0x65775a, 0x2b3329);
    grid.position.y = 0.006;
    grid.material.transparent = true;
    grid.material.opacity = 0.56;
    this.scene.add(grid);

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(80, 80),
      new THREE.ShadowMaterial({ color: 0x000000, opacity: 0.22 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.005;
    floor.receiveShadow = true;
    floor.name = 'Editor_Ground';
    this.scene.add(floor);

    const origin = new THREE.Mesh(
      new THREE.RingGeometry(0.16, 0.21, 48),
      new THREE.MeshBasicMaterial({ color: 0xc8ff72, transparent: true, opacity: 0.72, side: THREE.DoubleSide }),
    );
    origin.rotation.x = -Math.PI / 2;
    origin.position.y = 0.012;
    origin.name = 'Editor_Origin';
    this.scene.add(origin);
  }

  #createDemoScene() {
    const main = this.addMass(deepCopy(STARTER_MASS), { commit: false, select: false });
    this.selectMass(main);
  }

  #bindPointerEvents() {
    this.canvas.addEventListener(
      'pointerdown',
      (event) => {
        if (event.button !== 0) return;
        const handled = this.inputDown(event.clientX, event.clientY, 'pointer');
        if (handled) {
          this.pointerId = event.pointerId;
          this.canvas.setPointerCapture?.(event.pointerId);
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      { passive: false },
    );

    this.canvas.addEventListener(
      'pointermove',
      (event) => {
        if (!this.dragState || (this.pointerId !== null && event.pointerId !== this.pointerId)) return;
        this.inputMove(event.clientX, event.clientY, 'pointer');
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      { passive: false },
    );

    const finish = (event) => {
      if (!this.dragState || (this.pointerId !== null && event.pointerId !== this.pointerId)) return;
      this.inputUp(event.clientX, event.clientY, 'pointer');
      if (this.pointerId !== null) {
        this.canvas.releasePointerCapture?.(this.pointerId);
      }
      this.pointerId = null;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    this.canvas.addEventListener('pointerup', finish, { passive: false });
    this.canvas.addEventListener('pointercancel', finish, { passive: false });
  }

  #animate() {
    if (this.isDisposed) return;
    this.controls.update();
    if (this.selectionHelper) this.selectionHelper.update();
    this.renderer.render(this.scene, this.camera);
    this.animationFrame = requestAnimationFrame(() => this.#animate());
  }

  resize() {
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const expectedWidth = Math.floor(width * pixelRatio);
    const expectedHeight = Math.floor(height * pixelRatio);

    if (this.canvas.width !== expectedWidth || this.canvas.height !== expectedHeight) {
      this.renderer.setPixelRatio(pixelRatio);
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    }
  }

  resetView() {
    this.camera.position.set(13.4, 10.6, 15.2);
    this.controls.target.set(0, 3.35, 0);
    this.controls.update();
    this.callbacks.onStatus('Вид камеры восстановлен.');
  }

  setTool(tool) {
    if (!TOOL_COPY[tool]) return;
    this.tool = tool;
    this.dragState = null;
    this.controls.enabled = true;
    this.callbacks.onToolChange({ tool, ...TOOL_COPY[tool] });
    this.callbacks.onStatus(TOOL_COPY[tool].help);
  }

  setOpeningSize(values) {
    const next = {
      width: clamp(snap(finiteOr(values.width, this.opening.width)), 0.3, 20),
      height: clamp(snap(finiteOr(values.height, this.opening.height)), 0.3, 20),
      sill: clamp(snap(finiteOr(values.sill, this.opening.sill)), 0, 20),
    };
    this.opening = next;
    return deepCopy(next);
  }

  addMass(values = {}, options = {}) {
    const data = this.#normalizeMass({
      id: values.id ?? this.#makeId(),
      name: values.name ?? `Масса ${String(++this.massCounter).padStart(2, '0')}`,
      width: values.width ?? 4,
      depth: values.depth ?? 4,
      height: values.height ?? 3,
      position: values.position ?? { x: 0, z: 0 },
      rotationY: values.rotationY ?? 0,
      profile: values.profile ?? 'box',
      eaveRatio: values.eaveRatio ?? 0.72,
      holes: values.holes ?? [],
    });

    this.#syncCounterFromName(data.name);
    const mesh = this.#makeMassMesh(data);
    this.massesGroup.add(mesh);

    if (options.select !== false) this.selectMass(mesh);
    if (options.commit !== false) this.#commitHistory();
    this.callbacks.onModelChange(this.serializeProject());
    this.callbacks.onStatus(`${data.name} создан.`);
    return mesh;
  }

  deleteSelected() {
    if (!this.selected) {
      this.callbacks.onStatus('Сначала выберите объём.');
      return false;
    }

    const name = this.selected.userData.mass.name;
    this.#disposeMass(this.selected);
    this.massesGroup.remove(this.selected);
    this.selectMass(null);
    this.#commitHistory();
    this.callbacks.onModelChange(this.serializeProject());
    this.callbacks.onStatus(`${name} удалён.`);
    return true;
  }


  duplicateSelected() {
    if (!this.selected) {
      this.callbacks.onStatus('Сначала выберите объём.');
      return false;
    }

    const source = deepCopy(this.selected.userData.mass);
    const copy = this.addMass({
      ...source,
      id: this.#makeId(),
      name: `${source.name} — копия`.slice(0, 80),
      position: {
        x: source.position.x + 0.6,
        z: source.position.z + 0.6,
      },
      holes: deepCopy(source.holes),
    });
    this.callbacks.onStatus(`${copy.userData.mass.name} создана.`);
    return true;
  }

  newProject() {
    this.massCounter = 0;
    const id = this.#makeId();
    this.loadProject(
      {
        format: PROJECT_FORMAT,
        version: PROJECT_VERSION,
        units: 'm',
        selectedId: id,
        masses: [{ ...deepCopy(STARTER_MASS), id }],
      },
      { resetHistory: true },
    );
    this.callbacks.onStatus('Создан новый проект со стартовой массой.');
    return true;
  }

  rotateSelected(degrees = 15) {
    if (!this.selected) {
      this.callbacks.onStatus('Сначала выберите объём.');
      return false;
    }

    const data = this.selected.userData.mass;
    data.rotationY = snap(data.rotationY + THREE.MathUtils.degToRad(degrees), THREE.MathUtils.degToRad(1));
    this.#applyMassTransform(this.selected);
    this.#commitHistory();
    this.#notifySelection();
    this.callbacks.onModelChange(this.serializeProject());
    this.callbacks.onStatus(`Поворот: ${Math.round(THREE.MathUtils.radToDeg(data.rotationY))}°.`);
    return true;
  }

  updateSelected(values) {
    if (!this.selected) return false;

    const current = this.selected.userData.mass;
    const next = this.#normalizeMass({
      ...current,
      ...values,
      position: {
        x: values.position?.x ?? current.position.x,
        z: values.position?.z ?? current.position.z,
      },
      holes: current.holes,
    });

    current.name = next.name;
    current.width = next.width;
    current.depth = next.depth;
    current.height = next.height;
    current.position = next.position;
    current.rotationY = next.rotationY;
    current.profile = next.profile;
    current.eaveRatio = next.eaveRatio;
    current.holes = this.#fitHoles(next.holes, next);

    this.#rebuildMass(this.selected);
    this.#applyMassTransform(this.selected);
    this.#commitHistory();
    this.#notifySelection();
    this.callbacks.onModelChange(this.serializeProject());
    this.callbacks.onStatus('Параметры объёма обновлены.');
    return true;
  }

  selectMass(mesh) {
    if (this.selected === mesh) {
      this.#notifySelection();
      return;
    }

    if (this.selected) this.#setSelectedVisual(this.selected, false);
    this.selected = mesh && this.massesGroup.children.includes(mesh) ? mesh : null;

    if (this.selectionHelper) {
      this.scene.remove(this.selectionHelper);
      this.selectionHelper.geometry?.dispose?.();
      this.selectionHelper.material?.dispose?.();
      this.selectionHelper = null;
    }

    if (this.selected) {
      this.#setSelectedVisual(this.selected, true);
      this.callbacks.onStatus(`${this.selected.userData.mass.name} выбран.`);
    }

    this.#notifySelection();
  }

  inputDown(clientX, clientY, source = 'external') {
    if (!this.#pointInsideViewport(clientX, clientY)) return false;

    const hit = this.#raycastMasses(clientX, clientY);
    if (!hit) {
      this.selectMass(null);
      return false;
    }

    const mesh = hit.object;
    this.selectMass(mesh);
    this.controls.enabled = false;

    if (this.tool === 'move') {
      const ground = this.#intersectGround(clientX, clientY);
      if (!ground) {
        this.controls.enabled = true;
        return false;
      }
      const data = mesh.userData.mass;
      this.dragState = {
        type: 'move',
        source,
        startProject: this.serializeProject(),
        offsetX: data.position.x - ground.x,
        offsetZ: data.position.z - ground.z,
        changed: false,
      };
      this.callbacks.onStatus('Перемещение: отпустите pinch или кнопку мыши для фиксации.');
      return true;
    }

    if (this.tool === 'height') {
      this.dragState = {
        type: 'height',
        source,
        startProject: this.serializeProject(),
        startY: clientY,
        startHeight: mesh.userData.mass.height,
        changed: false,
      };
      this.callbacks.onStatus('Push/pull: ведите вверх или вниз.');
      return true;
    }

    if (this.tool === 'cut') {
      const changed = this.#cutOpening(mesh, hit.point);
      this.dragState = { type: 'cut', source, changed };
      this.controls.enabled = true;
      return true;
    }

    this.controls.enabled = true;
    return false;
  }

  inputMove(clientX, clientY) {
    if (!this.dragState || !this.selected) return false;

    const data = this.selected.userData.mass;

    if (this.dragState.type === 'move') {
      const ground = this.#intersectGround(clientX, clientY);
      if (!ground) return false;
      const nextX = snap(ground.x + this.dragState.offsetX);
      const nextZ = snap(ground.z + this.dragState.offsetZ);
      if (nextX !== data.position.x || nextZ !== data.position.z) {
        data.position.x = nextX;
        data.position.z = nextZ;
        this.dragState.changed = true;
        this.#applyMassTransform(this.selected);
        this.#notifySelection();
        this.callbacks.onStatus(`X ${nextX.toFixed(1)} м · Z ${nextZ.toFixed(1)} м`);
      }
      return true;
    }

    if (this.dragState.type === 'height') {
      const rect = this.canvas.getBoundingClientRect();
      const metersPerPixel = 12 / Math.max(360, rect.height);
      const delta = (this.dragState.startY - clientY) * metersPerPixel;
      const nextHeight = clamp(snap(this.dragState.startHeight + delta), MIN_SIZE, MAX_SIZE);
      if (nextHeight !== data.height) {
        data.height = nextHeight;
        data.holes = this.#fitHoles(data.holes, data);
        this.dragState.changed = true;
        this.#rebuildMass(this.selected);
        this.#notifySelection();
        this.callbacks.onStatus(`Высота ${nextHeight.toFixed(1)} м`);
      }
      return true;
    }

    return this.dragState.type === 'cut';
  }

  inputUp() {
    if (!this.dragState) return false;
    const { type, changed } = this.dragState;
    this.dragState = null;
    this.controls.enabled = true;

    if (changed && (type === 'move' || type === 'height')) {
      this.#commitHistory();
      this.callbacks.onModelChange(this.serializeProject());
      this.callbacks.onStatus(type === 'move' ? 'Положение зафиксировано.' : 'Высота зафиксирована.');
    }
    return true;
  }

  cancelExternalInteraction() {
    if (!this.dragState) return;
    if (this.dragState.startProject) {
      this.loadProject(this.dragState.startProject, { commit: false });
    }
    this.dragState = null;
    this.controls.enabled = true;
  }

  undo() {
    if (this.history.length <= 1) return false;
    const current = this.history.pop();
    this.future.push(current);
    this.loadProject(JSON.parse(this.history.at(-1)), { commit: false });
    this.#notifyHistory();
    this.callbacks.onStatus('Действие отменено.');
    return true;
  }

  redo() {
    if (!this.future.length) return false;
    const snapshot = this.future.pop();
    this.history.push(snapshot);
    this.loadProject(JSON.parse(snapshot), { commit: false });
    this.#notifyHistory();
    this.callbacks.onStatus('Действие повторено.');
    return true;
  }

  serializeProject() {
    return {
      format: PROJECT_FORMAT,
      version: PROJECT_VERSION,
      units: 'm',
      selectedId: this.selected?.userData.mass.id ?? null,
      masses: this.massesGroup.children
        .filter((object) => object.isMesh)
        .map((mesh) => deepCopy(mesh.userData.mass)),
    };
  }

  loadProject(project, options = {}) {
    const supportedFormat = project?.format === PROJECT_FORMAT || project?.format === 'airform-project';
    if (!project || !supportedFormat || !Array.isArray(project.masses)) {
      throw new Error('Некорректный формат проекта Massa.');
    }

    this.selectMass(null);
    for (const mesh of [...this.massesGroup.children]) {
      this.#disposeMass(mesh);
      this.massesGroup.remove(mesh);
    }

    let selected = null;
    for (const rawMass of project.masses) {
      const mass = this.addMass(rawMass, { commit: false, select: false });
      if (rawMass.id === project.selectedId) selected = mass;
    }
    this.selectMass(selected);

    if (options.resetHistory) {
      this.history = [];
      this.future = [];
      this.#commitHistory(true);
    } else if (options.commit) {
      this.#commitHistory();
    }
    this.callbacks.onModelChange(this.serializeProject());
  }

  async exportGLB() {
    const exportRoot = new THREE.Group();
    exportRoot.name = 'Massa_Project';
    exportRoot.userData = { units: 'm', generator: GENERATOR };

    for (const mesh of this.massesGroup.children.filter((object) => object.isMesh)) {
      const clone = new THREE.Mesh(mesh.geometry.clone(), mesh.material.clone());
      clone.name = mesh.userData.mass.name;
      clone.position.copy(mesh.position);
      clone.rotation.copy(mesh.rotation);
      clone.scale.copy(mesh.scale);
      clone.updateMatrix();
      clone.castShadow = false;
      clone.receiveShadow = false;
      clone.userData = { massa: deepCopy(mesh.userData.mass) };
      exportRoot.add(clone);
    }

    const exporter = new GLTFExporter();
    try {
      const result = await exporter.parseAsync(exportRoot, {
        binary: true,
        onlyVisible: true,
        trs: false,
        includeCustomExtensions: true,
      });
      if (!(result instanceof ArrayBuffer)) {
        throw new Error('Экспортёр не вернул бинарный GLB.');
      }
      return result;
    } finally {
      exportRoot.traverse((object) => {
        object.geometry?.dispose?.();
        if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose?.());
        else object.material?.dispose?.();
      });
    }
  }


  exportOBJ() {
    const exportRoot = new THREE.Group();
    exportRoot.name = 'Massa_Project';

    for (const mesh of this.massesGroup.children.filter((object) => object.isMesh)) {
      const clone = new THREE.Mesh(mesh.geometry.clone(), new THREE.MeshBasicMaterial({ color: 0xffffff }));
      clone.name = mesh.userData.mass.name;
      clone.position.copy(mesh.position);
      clone.rotation.copy(mesh.rotation);
      clone.scale.copy(mesh.scale);
      clone.updateMatrixWorld(true);
      exportRoot.add(clone);
    }

    exportRoot.updateMatrixWorld(true);
    const exporter = new OBJExporter();
    try {
      return `# ${GENERATOR}
# Units: metres
${exporter.parse(exportRoot)}`;
    } finally {
      exportRoot.traverse((object) => {
        object.geometry?.dispose?.();
        if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose?.());
        else object.material?.dispose?.();
      });
    }
  }


  async capturePNG() {
    this.renderer.render(this.scene, this.camera);
    const blob = await new Promise((resolve, reject) => {
      this.canvas.toBlob((result) => {
        if (result) resolve(result);
        else reject(new Error('Браузер не сформировал изображение PNG.'));
      }, 'image/png');
    });
    return blob;
  }

  dispose() {
    this.isDisposed = true;
    cancelAnimationFrame(this.animationFrame);
    this.resizeObserver?.disconnect();
    this.controls?.dispose();
    for (const mesh of [...this.massesGroup.children]) this.#disposeMass(mesh);
    this.renderer.dispose();
  }

  #cutOpening(mesh, worldPoint) {
    const data = mesh.userData.mass;
    const local = mesh.worldToLocal(worldPoint.clone());
    const width = this.opening.width;
    const height = this.opening.height;
    const sill = this.opening.sill;
    const margin = 0.12;
    const grounded = sill <= 0.05;
    const facadeHeight = data.profile === 'gable' ? data.height * data.eaveRatio : data.height;

    if (width + margin * 2 >= data.width || height + (grounded ? 0 : sill) + margin >= facadeHeight) {
      this.callbacks.onStatus('Проём не помещается: уменьшите его или увеличьте объём.');
      return false;
    }

    const centerX = clamp(snap(local.x), -data.width / 2 + width / 2 + margin, data.width / 2 - width / 2 - margin);
    const centerY = grounded ? height / 2 : sill + height / 2;
    const hole = { x: centerX, y: centerY, width, height, grounded };

    const overlaps = data.holes.some((existing) => this.#rectanglesOverlap(existing, hole, 0.08));
    if (overlaps) {
      this.callbacks.onStatus('Новый проём пересекается с существующим.');
      return false;
    }

    data.holes.push(hole);
    this.#rebuildMass(mesh);
    this.#commitHistory();
    this.#notifySelection();
    this.callbacks.onModelChange(this.serializeProject());
    this.callbacks.onStatus(`Проём ${width.toFixed(1)} × ${height.toFixed(1)} м создан.`);
    return true;
  }

  #rectanglesOverlap(a, b, gap = 0) {
    return !(
      a.x + a.width / 2 + gap <= b.x - b.width / 2 ||
      b.x + b.width / 2 + gap <= a.x - a.width / 2 ||
      a.y + a.height / 2 + gap <= b.y - b.height / 2 ||
      b.y + b.height / 2 + gap <= a.y - a.height / 2
    );
  }

  #normalizeMass(raw) {
    const width = clamp(snap(finiteOr(raw.width, 4)), MIN_SIZE, MAX_SIZE);
    const depth = clamp(snap(finiteOr(raw.depth, 4)), MIN_SIZE, MAX_SIZE);
    const height = clamp(snap(finiteOr(raw.height, 3)), MIN_SIZE, MAX_SIZE);
    const profile = raw.profile === 'gable' ? 'gable' : 'box';
    const eaveRatio = profile === 'gable' ? clamp(finiteOr(raw.eaveRatio, 0.72), 0.45, 0.92) : 1;
    const mass = {
      id: String(raw.id ?? this.#makeId()),
      name: String(raw.name ?? 'Масса').trim().slice(0, 80) || 'Масса',
      width,
      depth,
      height,
      profile,
      eaveRatio,
      position: {
        x: snap(finiteOr(raw.position?.x, 0)),
        z: snap(finiteOr(raw.position?.z, 0)),
      },
      rotationY: finiteOr(raw.rotationY, 0),
      holes: Array.isArray(raw.holes)
        ? raw.holes.map((hole) => {
            const holeHeight = clamp(snap(finiteOr(hole.height, 1.5)), 0.2, MAX_SIZE);
            const holeY = snap(finiteOr(hole.y, holeHeight / 2 + 0.9));
            const grounded = Boolean(hole.grounded) || holeY - holeHeight / 2 <= 0.05;
            return {
              x: snap(finiteOr(hole.x, 0)),
              y: holeY,
              width: clamp(snap(finiteOr(hole.width, 1.2)), 0.2, MAX_SIZE),
              height: holeHeight,
              grounded,
            };
          })
        : [],
    };
    mass.holes = this.#fitHoles(mass.holes, mass);
    return mass;
  }

  #fitHoles(holes, mass) {
    const margin = 0.12;
    const fitted = [];
    const facadeHeight = mass.profile === 'gable' ? mass.height * mass.eaveRatio : mass.height;
    for (const raw of holes) {
      const grounded = Boolean(raw.grounded);
      const width = Math.min(raw.width, mass.width - margin * 2);
      const maxHoleHeight = Math.max(0.2, facadeHeight - (grounded ? margin : margin * 2));
      const height = Math.min(raw.height, maxHoleHeight);
      if (width < 0.2 || height < 0.2) continue;
      const hole = {
        width: snap(width),
        height: snap(height),
        x: clamp(snap(raw.x), -mass.width / 2 + width / 2 + margin, mass.width / 2 - width / 2 - margin),
        y: grounded
          ? snap(height / 2)
          : clamp(snap(raw.y), height / 2 + margin, facadeHeight - height / 2 - margin),
        grounded,
      };
      if (!fitted.some((existing) => this.#rectanglesOverlap(existing, hole, 0.02))) fitted.push(hole);
    }
    return fitted;
  }

  #makeMassMesh(data) {
    const geometry = this.#makeMassGeometry(data);
    const material = new THREE.MeshStandardMaterial({
      color: 0xe2e4dc,
      roughness: 0.72,
      metalness: 0.015,
      emissive: 0x000000,
      emissiveIntensity: 0,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = data.name;
    mesh.userData.mass = data;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.#applyMassTransform(mesh);
    this.#attachEdges(mesh);
    return mesh;
  }

  #makeMassGeometry(data) {
    const halfWidth = data.width / 2;
    const shape = new THREE.Shape();
    const groundedOpenings = data.holes
      .filter((hole) => hole.grounded)
      .sort((a, b) => a.x - b.x);

    shape.moveTo(-halfWidth, 0);
    for (const opening of groundedOpenings) {
      const left = opening.x - opening.width / 2;
      const right = opening.x + opening.width / 2;
      const top = opening.height;
      shape.lineTo(left, 0);
      shape.lineTo(left, top);
      shape.lineTo(right, top);
      shape.lineTo(right, 0);
    }
    shape.lineTo(halfWidth, 0);

    if (data.profile === 'gable') {
      const eaveHeight = data.height * data.eaveRatio;
      shape.lineTo(halfWidth, eaveHeight);
      shape.lineTo(0, data.height);
      shape.lineTo(-halfWidth, eaveHeight);
    } else {
      shape.lineTo(halfWidth, data.height);
      shape.lineTo(-halfWidth, data.height);
    }
    shape.closePath();

    for (const hole of data.holes.filter((entry) => !entry.grounded)) {
      const halfHoleWidth = hole.width / 2;
      const halfHoleHeight = hole.height / 2;
      const path = new THREE.Path();
      path.moveTo(hole.x - halfHoleWidth, hole.y - halfHoleHeight);
      path.lineTo(hole.x - halfHoleWidth, hole.y + halfHoleHeight);
      path.lineTo(hole.x + halfHoleWidth, hole.y + halfHoleHeight);
      path.lineTo(hole.x + halfHoleWidth, hole.y - halfHoleHeight);
      path.closePath();
      shape.holes.push(path);
    }

    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: data.depth,
      steps: 1,
      bevelEnabled: false,
      curveSegments: 1,
    });
    geometry.translate(0, 0, -data.depth / 2);
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    return geometry;
  }

  #attachEdges(mesh) {
    const edgeGeometry = new THREE.EdgesGeometry(mesh.geometry, 24);
    const edgeMaterial = new THREE.LineBasicMaterial({ color: 0x596055, transparent: true, opacity: 0.58 });
    const edges = new THREE.LineSegments(edgeGeometry, edgeMaterial);
    edges.name = 'Editor_Edges';
    edges.raycast = () => {};
    mesh.add(edges);
    mesh.userData.edgeLines = edges;
  }

  #rebuildMass(mesh) {
    const selected = mesh === this.selected;
    const oldEdges = mesh.userData.edgeLines;
    if (oldEdges) {
      mesh.remove(oldEdges);
      oldEdges.geometry.dispose();
      oldEdges.material.dispose();
    }
    mesh.geometry.dispose();
    mesh.geometry = this.#makeMassGeometry(mesh.userData.mass);
    mesh.name = mesh.userData.mass.name;
    this.#attachEdges(mesh);
    this.#setSelectedVisual(mesh, selected);
    this.#applyMassTransform(mesh);
    this.selectionHelper?.update();
  }

  #applyMassTransform(mesh) {
    const data = mesh.userData.mass;
    mesh.position.set(data.position.x, 0, data.position.z);
    mesh.rotation.set(0, data.rotationY, 0);
    mesh.updateMatrixWorld(true);
    this.selectionHelper?.update();
  }

  #setSelectedVisual(mesh, selected) {
    if (!mesh?.material) return;
    mesh.material.emissive.setHex(selected ? 0x2b3e14 : 0x000000);
    mesh.material.emissiveIntensity = selected ? 0.62 : 0;
    const edges = mesh.userData.edgeLines;
    if (edges) {
      edges.material.color.setHex(selected ? 0xc8ff72 : 0x596055);
      edges.material.opacity = selected ? 0.92 : 0.58;
    }
  }

  #disposeMass(mesh) {
    mesh.traverse((object) => {
      if (object === mesh) return;
      object.geometry?.dispose?.();
      if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose?.());
      else object.material?.dispose?.();
    });
    mesh.geometry?.dispose?.();
    if (Array.isArray(mesh.material)) mesh.material.forEach((material) => material.dispose?.());
    else mesh.material?.dispose?.();
  }

  #raycastMasses(clientX, clientY) {
    this.#setRay(clientX, clientY);
    const targets = this.massesGroup.children.filter((object) => object.isMesh);
    const hits = this.raycaster.intersectObjects(targets, false);
    return hits[0] ?? null;
  }

  #intersectGround(clientX, clientY) {
    this.#setRay(clientX, clientY);
    return this.raycaster.ray.intersectPlane(this.groundPlane, this.tempPoint)?.clone() ?? null;
  }

  #setRay(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    this.pointerNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.pointerNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);
  }

  #pointInsideViewport(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  }

  #commitHistory(force = false) {
    const snapshot = JSON.stringify(this.serializeProject());
    if (!force && snapshot === this.history.at(-1)) {
      this.#notifyHistory();
      return;
    }
    this.history.push(snapshot);
    if (this.history.length > MAX_HISTORY) this.history.shift();
    this.future = [];
    this.#notifyHistory();
  }

  #notifyHistory() {
    this.callbacks.onHistoryChange({
      canUndo: this.history.length > 1,
      canRedo: this.future.length > 0,
    });
  }

  #notifySelection() {
    const data = this.selected ? deepCopy(this.selected.userData.mass) : null;
    this.callbacks.onSelectionChange(data);
  }

  #makeId() {
    if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
    return `mass-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
  }

  #syncCounterFromName(name) {
    const match = String(name).match(/(\d+)\s*$/);
    if (match) this.massCounter = Math.max(this.massCounter, Number(match[1]));
  }
}

export { TOOL_COPY };
